import { z } from "zod";

/**
 * Single source of truth for "does this password meet policy" — reused by
 * every place a new password gets set: self-service change password,
 * reset-password-via-token, admin create-user, and admin reset-a-user's-
 * password, on both the main app and the admin portal. Previously each of
 * those had its own copy-pasted min-length/regex, and they'd drifted (some
 * required 8 characters, some silently required 10, some had no complexity
 * rule at all) — this is the one place to change the policy going forward.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 70;
export const PASSWORD_SPECIAL_CHARS = "#?!@$%^&*-";

const PASSWORD_REQUIREMENTS: Array<{
  test: (value: string) => boolean;
  label: string;
}> = [
  { test: (value) => /[a-z]/.test(value), label: "1 lowercase letter" },
  { test: (value) => /[A-Z]/.test(value), label: "1 uppercase letter" },
  { test: (value) => /[0-9]/.test(value), label: "1 number" },
  {
    test: (value) => new RegExp(`[${PASSWORD_SPECIAL_CHARS}]`).test(value),
    label: `1 special character ${PASSWORD_SPECIAL_CHARS}`,
  },
];

/**
 * Returns the policy error for `value`, or undefined when it's valid.
 * Reports only what's still unmet (e.g. once length is satisfied, a
 * missing-uppercase password doesn't also repeat "too short") — surfacing
 * everything at once regardless of what was already fixed reads as broken
 * UX to a user typing an otherwise-valid password.
 */
export function passwordPolicyError(value: string): string | undefined {
  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  if (value.length > PASSWORD_MAX_LENGTH) {
    return `Password must be at most ${PASSWORD_MAX_LENGTH} characters`;
  }

  const missing = PASSWORD_REQUIREMENTS.filter(
    (requirement) => !requirement.test(value),
  ).map((requirement) => requirement.label);

  return missing.length > 0
    ? `Password must contain ${missing.join(", ")}`
    : undefined;
}

/** zod wrapper around passwordPolicyError, for react-hook-form + zodResolver flows. */
export const passwordSchema = z.string().superRefine((value, ctx) => {
  const message = passwordPolicyError(value);
  if (message) ctx.addIssue(message);
});
